import { localDatabaseUrls } from "../backend/constants";
import { postAPI } from "../backend/commons";
import { getJWTToken } from "../shared/fetchWrapper/fetchWrapper";
import AlertManager from "../shared/Alert/Alert";

const toNumericValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const normalizedValue = Number(value);
  return Number.isFinite(normalizedValue) ? normalizedValue : null;
};

const normalizeBatchId = (batchId) => {
  if (batchId === null || batchId === undefined || batchId === "") {
    return "";
  }

  const numericBatchId = Number(batchId);
  return Number.isFinite(numericBatchId) && String(numericBatchId) === String(batchId).trim()
    ? numericBatchId
    : batchId;
};

const formatPercentage = (value) => {
  const numericValue = toNumericValue(value);
  return numericValue === null ? "" : String(numericValue);
};

const formatDollar = (value) => {
  const numericValue = toNumericValue(value);
  return numericValue === null ? "" : numericValue.toFixed(2);
};

const resolveThresholdChange = (row, originalRow = {}) => {
  const candidates = [
    {
      field: "modifyNewMarginPercent",
      requestKey: "newMarginPercentage",
      comment: (value) => `Increase margin to ${formatPercentage(value)}%`,
    },
    {
      field: "modifyNewMargin",
      requestKey: "newMarginDollar",
      comment: (value) => `Set margin to $${formatDollar(value)}`,
    },
    {
      field: "modifyNewDc",
      requestKey: "dcCostPercentage",
      comment: (value) => `Set new DC % to ${formatPercentage(value)}%`,
    },
    {
      field: "modifyProposedDc",
      requestKey: "dcCostDollar",
      comment: (value) => `Set DC cost to $${formatDollar(value)}`,
    },
    {
      field: "newMarginPercent",
      requestKey: "newMarginPercentage",
      comment: (value) => `Increase margin to ${formatPercentage(value)}%`,
    },
    {
      field: "newMargin",
      requestKey: "newMarginDollar",
      comment: (value) => `Set margin to $${formatDollar(value)}`,
    },
    {
      field: "modifyMarginThreshold",
      requestKey: "dcCostPercentage",
      comment: (value) => `Set new DC % to ${formatPercentage(value)}%`,
    },
    {
      field: "proposedDc",
      requestKey: "dcCostDollar",
      comment: (value) => `Set DC cost to $${formatDollar(value)}`,
    },
    {
      field: "dcCostPercentage",
      requestKey: "dcCostPercentage",
      comment: (value) => `Set new DC % to ${formatPercentage(value)}%`,
    },
    {
      field: "dcCostDollar",
      requestKey: "dcCostDollar",
      comment: (value) => `Set DC cost to $${formatDollar(value)}`,
    },
  ];

  for (const candidate of candidates) {
    const currentValue = toNumericValue(row?.[candidate.field]);
    const originalValue = toNumericValue(originalRow?.[candidate.field]);

    if (currentValue !== null && currentValue !== originalValue) {
      return {
        field: candidate.field,
        requestKey: candidate.requestKey,
        value: currentValue,
        comment: candidate.comment(currentValue),
      };
    }
  }

  for (const candidate of candidates) {
    const currentValue = toNumericValue(row?.[candidate.field]);
    if (currentValue !== null) {
      return {
        field: candidate.field,
        requestKey: candidate.requestKey,
        value: currentValue,
        comment: candidate.comment(currentValue),
      };
    }
  }

  const currentStrategy = row?.modifyMarginStrategyType ?? row?.marginStrategyType ?? "";
  const originalStrategy = originalRow?.modifyMarginStrategyType ?? originalRow?.marginStrategyType ?? "";

  if (currentStrategy && currentStrategy !== originalStrategy) {
    return {
      field: "modifyMarginStrategyType",
      requestKey: null,
      value: currentStrategy,
      comment: `Set margin strategy to ${currentStrategy}`,
    };
  }

  return null;
};

const resolveSku = (row) => row?.parsedSku || row?.skuId || row?.sku || row?.id || "";

const resolveComment = (change) => change?.comment || "Threshold updated";

const resolveModifiedThresholdValues = (row = {}) => ({
  modifiedMarginStrategy: row?.modifyMarginStrategyType ?? row?.modifiedMarginStrategy ?? row?.marginStrategyType ?? "",
  modifiedMarginPercentage: toNumericValue(
    row?.modifyNewMarginPercent ?? row?.modifiedMarginPercentage ?? row?.newMarginPercent ?? row?.newMarginPercentage
  ),
  modifiedMarginDollar: toNumericValue(
    row?.modifyNewMargin ?? row?.modifiedMarginDollar ?? row?.newMargin ?? row?.newMarginDollar ?? row?.marginDollar
  ),
  modifiedDcCost: toNumericValue(
    row?.modifyProposedDc ?? row?.modifiedDcCost ?? row?.proposedDc ?? row?.dcDollar ?? row?.dcCostDollar
  ),
  modifiedDcPercentage: toNumericValue(
    row?.modifyNewDc ?? row?.modifiedDcPercentage ?? row?.modifyMarginThreshold ?? row?.dcPercent ?? row?.dcCostPercentage
  ),
});

export const buildThresholdUpdatePayload = (rows = [], originalRows = [], batchId) => {
  if (!Array.isArray(rows) || !rows.length) {
    return [];
  }

  const originalRowsById = new Map(
    (Array.isArray(originalRows) ? originalRows : []).map((row) => [row?.id, row])
  );
  const resolvedBatchId = normalizeBatchId(batchId);

  return rows
    .map((row) => {
      const originalRow = originalRowsById.get(row?.id) || {};
      const change = resolveThresholdChange(row, originalRow);

      if (!change) {
        return null;
      }

      const payload = {
        sku: resolveSku(row),
        hqLine: row?.hqLine || "",
        batchId: resolvedBatchId,
        comments: resolveComment(change),
        ...resolveModifiedThresholdValues(row),
      };
      return payload;
    })
    .filter(Boolean);
};

export const modifyDCCostThreshold = async ({ batchId, rows = [], originalRows = [] } = {}) => {
  if (!batchId) {
    AlertManager.error("Unable to update DC cost thresholds.", { contactSupport: false });
    return null;
  }

  if (!Array.isArray(rows) || !rows.length) {
    return null;
  }

  const tokenInfo = await getJWTToken();
  if (!tokenInfo) {
    window.location.href = "/";
    return null;
  }

  const payloads = buildThresholdUpdatePayload(rows, originalRows, batchId);
  if (!payloads.length) {
    AlertManager.warning("Make a threshold change before submitting.", { contactSupport: false });
    return null;
  }

  const apiUrl = `${localDatabaseUrls.baseURL}api/v1/dc-cost/modify-threshold`;
  const responseBodies = [];

  try {
    for (const payload of payloads) {
      const requestBody = new Blob(
        [JSON.stringify({ ...payload, updatedBy: tokenInfo.username || tokenInfo.email || "" })],
        { type: "application/json" }
      );
      const response = await postAPI(apiUrl, requestBody, tokenInfo);

      if (!response?.ok) {
        const errorData = await response?.json?.().catch(() => ({}));
        throw new Error(errorData?.message || "Failed to update DC cost thresholds.");
      }

      const responseBody = await response?.json?.().catch(() => null);
      if (responseBody) {
        responseBodies.push(responseBody);
      }
    }

    AlertManager.success("DC cost thresholds updated successfully.", { contactSupport: false });
    return responseBodies.length > 1 ? responseBodies : responseBodies[0] || null;
  } catch (error) {
    AlertManager.error(error?.message || "Failed to update DC cost thresholds.", { contactSupport: false });
    return null;
  }
};

export default modifyDCCostThreshold;
